'use client'

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import '../../styles/toast.css'

export type ToastType = 'success' | 'error' | 'warning' | 'info'
export type ToastVariant =
  | 'classic'
  | 'compact'
  | 'soft'
  | 'solid'
  | 'outline'
  | 'accent'
  | 'glass'
  | 'split'
  | 'progress'
  | 'minimal'
  | 'premium'

interface ToastProps {
  message: ReactNode
  type?: ToastType
  duration?: number
  variant?: ToastVariant
  onClick?: () => void
  onClose: () => void
}

export function Toast({ message, type = 'info', duration = 3000, variant = 'classic', onClick, onClose }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    // Sticky toast: duration <= 0 (or non-finite) => keep until user closes/clicks.
    if (!Number.isFinite(duration) || (duration as number) <= 0) return

    const exitTimer = setTimeout(() => {
      setIsExiting(true)
    }, duration - 300)

    const closeTimer = setTimeout(() => {
      onClose()
    }, duration)

    return () => {
      clearTimeout(exitTimer)
      clearTimeout(closeTimer)
    }
  }, [duration, onClose])

  const handleClose = () => {
    setIsExiting(true)
    setTimeout(onClose, 300)
  }

  const handleClick = () => {
    if (!onClick) return
    try {
      onClick()
    } finally {
      handleClose()
    }
  }

  const icons = {
    success: <i className="fa-duotone fa-circle-check" />,
    error: <i className="fa-duotone fa-circle-xmark" />,
    warning: <i className="fa-duotone fa-triangle-exclamation" />,
    info: <i className="fa-duotone fa-circle-info" />,
  }

  return (
    <div
      className={`toast toast--${type} toast--variant-${variant} ${isExiting ? 'toast--exiting' : ''}`}
      onClick={onClick ? handleClick : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={{
        ...(onClick ? { cursor: 'pointer' } : {}),
        '--toast-duration-ms': `${Math.max(300, duration || 3000)}ms`,
      } as CSSProperties}
      onKeyDown={(e) => {
        if (!onClick) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
    >
      <div className="toast__icon">
        {icons[type]}
      </div>
      <div className="toast__message">{message}</div>
      <button
        className="toast__close"
        onClick={(e) => {
          e.stopPropagation()
          handleClose()
        }}
        aria-label="Kapat"
      >
        <i className="fa-solid fa-xmark" />
      </button>
    </div>
  )
}
